<template>
    <dialog :id="$attrs.id">
        <form class="geoDistanceConfig" @submit.prevent="emitGeoDistance">
          <div class="geoDistanceHeader d-flex justify-content-between align-items-center">
            <h1>GeoDistancia</h1>
            <div class="btn btn-light rounded-circle" @click="closeModal"><i class="far fa-times"></i></div>
          </div>
          <div class="geoDistanceLocation" @click="togglePasteLocation" v-show="!pasteLocation">
            <div class="d-flex w-100 border rounded-sm">
              <span class="w-50 p-1 rounded-left bg-light text-center">Latitud</span>
              <p class="w-50 m-0 py-1 px-2 rounded-right bg-white text-muted">{{ geoDistanceLocation.lat }}</p>
            </div>
            <div class="d-flex w-100 border rounded-sm">
              <span class="w-50 p-1 rounded-left bg-light text-center">Longitud</span>
              <p class="w-50 m-0 py-1 px-2 rounded-right bg-white text-muted">{{ geoDistanceLocation.lng }}</p>
            </div>
          </div>
          <div v-show="pasteLocation" class="geoDistanceLocationInput">
            <textarea
              name="geoDistanceLocation"
              id="geoDistanceLocationInput"
              class="border rounded"
              placeholder="Ubicación Ej: 23.634500, -102.552800"
              v-model.trim="locationPasted" @blur="blurLocationPasted" @keyup.enter="activeBlurTextareaLocation"></textarea>
          </div>
          <small v-show="locationFormatError" class="text-danger">Formato no válido. Deben ser dos valores numéricos separados por una coma.</small>
          <div class="geoDistanceRatio">
            <label for="geoDistanceRatio" class="text-md">Radio de búsqueda ({{ geoDistanceRatio }} Km)</label>
            <input type="range" class="custom-range" min="1" max="100" step="1" id="geoDistanceRatio" v-model="geoDistanceRatio">
            <div class="geoDistanceRatio__range"><span>1 Km</span><span>100 Km</span></div>
          </div>
          <button type="submit" class="btn btn-primary">
            <span>Confirmar ubicación</span>
            <i class="fas fa-map-marker-alt ml-2"></i></button>
        </form>
        <GmapMap
          v-if="mapProvider === 'GOOGLE'"
          :center="center"
          :zoom="zoom"
          style="width: 100%; height: 100%; margin: 0;"
          ref="mapRef"
          :options="{
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUI: false,
            gestureHandling: 'greedy'
          }"
          @click="handleMapClick"
        >
          <GmapMarker
            :position="geoDistanceLocation"
            :draggable="true"
            @dragend="handleMarkerDraggable"
          />
          <GmapCircle
            :center="geoDistanceLocation"
            :radius="geoDistanceRatio * 1000"
            :visible="true"
            :options="{fillColor: FILL_CIRCLE_COLOR, strokeColor: STROKE_CIRCLE_COLOR, fillOpacity: 0.2}"
          ></GmapCircle>
        </GmapMap>
        <!-- Use Mapbox instead -->
        <div v-else class="vue-map-container">
          <div ref="mapContainer" class="map-container"></div>
        </div>
    </dialog>
</template>

<script>
import 'mapbox-gl/dist/mapbox-gl.css'
import { Component, Vue, Watch } from 'vue-property-decorator'
import mapboxgl from 'mapbox-gl'
import { circle } from '@turf/circle'
import LogService from '@/services/support/LogService'
import userInfoStore from '@/store/userInfo'
import config from '@/config'
mapboxgl.accessToken = config.mapbox.accessToken

@Component
export default class FillgridGeoDistanceFilterMoal extends Vue {
  METERS_IN_KM = 1000
  FILL_CIRCLE_COLOR = '#009cdd'
  STROKE_CIRCLE_COLOR = '#bfe6f6'
  mapProvider = userInfoStore.userInfo.domain.mapProvider
  geoDistanceRatio = 1
  geoDistanceLocation = {
    lat: 0,
    lng: 0
  }
  center = {
    lat: 0,
    lng: 0
  }
  zoom = 0
  pasteLocation = false
  locationPasted = ''
  locationFormatError = false

  created () {
    navigator.geolocation.getCurrentPosition(
      geolocation => {
        this.geoDistanceLocation.lat = geolocation.coords.latitude
        this.geoDistanceLocation.lng = geolocation.coords.longitude
        this.center.lat = geolocation.coords.latitude
        this.center.lng = geolocation.coords.longitude
        this.zoom = 14
      },
      error => {
        LogService.warn('No se dio permiso de obtener la ubicación actual.' + error)
        const MEXICO_CENTER_LOCATION = {
          lat: 23.634500,
          lng: -102.552800
        }
        this.geoDistanceLocation = MEXICO_CENTER_LOCATION
        this.center = MEXICO_CENTER_LOCATION
        this.zoom = 5
      },
      { enableHighAccuracy: true }
    )
  }

  mounted () {
    if (this.mapProvider === 'GOOGLE') {
      this.$refs.mapRef.$mapPromise.then(map => {
        this.map = map
      })
    } else { // mapProvider is MAPBOX
      // Create Map
      let map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v11', // Replace with your preferred map style
        center: [-102.552800, 23.634500],
        zoom: 4,
        antialias: true
      })

      // Create Marker
      const locationMarker = new mapboxgl.Marker({
        color: 'red'
      }).setLngLat([-102.552800, 23.634500])
      locationMarker.addTo(map)

      map.on('load', () => {
        // Add circle as source
        map.addSource('polygon', {
          'type': 'geojson',
          'data': {
            'type': 'Feature',
            'geometry': {
              'type': 'Polygon',
              'coordinates': []
            }
          }
        })

        // Add circle layer
        map.addLayer({
          'id': 'polygon',
          'type': 'fill',
          'source': 'polygon',
          'layout': {},
          'paint': {
            'fill-color': '#5EC2E8',
            'fill-opacity': 0.3
          }
        })

        map.on('click', (e) => {
          const { lat, lng } = e.lngLat
          // Set marker position
          locationMarker.remove()
          locationMarker.setLngLat([lng, lat])
          locationMarker.addTo(this.map)

          // Set circle position and radius
          map.getSource('polygon').setData(circle([lng, lat], this.geoDistanceRatio))

          this.geoDistanceLocation = {
            lat: lat.toFixed(10),
            lng: lng.toFixed(10)
          }
        })

        this.map = map

        setTimeout(() => {
          this.map.setLayoutProperty('country-label', 'text-field', ['get', 'name_es'])
        }, 100)
      })
    }
  }

  @Watch('geoDistanceRatio')
  updateCircleRadius () {
    this.map.getSource('polygon').setData(circle([this.geoDistanceLocation.lng, this.geoDistanceLocation.lat], this.geoDistanceRatio))
  }

  emitGeoDistance () {
    const geoDistance = {
      location: {
        latitude: this.geoDistanceLocation.lat,
        longitude: this.geoDistanceLocation.lng
      },
      kilometers: this.geoDistanceRatio
    }
    this.$emit('geoDistance', geoDistance)
    this.closeModal()
  }

  closeModal () {
    const modal = document.getElementById(this.$attrs.id)
    modal.close()
  }

  resize () {
    if (this.mapProvider !== 'GOOGLE') {
      setTimeout(() => {
        this.map.resize()
      }, 100)
    }
  }

  togglePasteLocation () {
    this.pasteLocation = !this.pasteLocation
    if (this.pasteLocation) {
      setTimeout(() => {
        const textareaInput = document.getElementById('geoDistanceLocationInput')
        textareaInput.focus()
      }, 0)
    } else {
      setTimeout(() => {
        this.locationFormatError = false
      }, 6000)
    }
  }

  blurLocationPasted () {
    const latLng = this.locationPasted.split(',')
    if (latLng.length === 2) {
      this.geoDistanceLocation.lat = Number(parseFloat(latLng[0]).toFixed(10))
      this.geoDistanceLocation.lng = Number(parseFloat(latLng[1]).toFixed(10))
      this.panToMarker()
    } else {
      this.locationFormatError = true
    }

    this.locationPasted = ''
    this.togglePasteLocation()
  }

  activeBlurTextareaLocation (event) {
    event.target.blur()
  }

  updateAndFocusLocation (event) {
    this.geoDistanceLocation = {
      lat: parseFloat(event.latLng.lat().toFixed(10)),
      lng: parseFloat(event.latLng.lng().toFixed(10))
    }
    this.panToMarker()
  }

  panToMarker () {
    this.$refs.mapRef.panTo(this.geoDistanceLocation)
  }

  handleMapClick (event) {
    this.updateAndFocusLocation(event)
  }

  handleMarkerDraggable (event) {
    this.updateAndFocusLocation(event)
  }
}
</script>

<style scoped>

.vue-map-container {
  width: 100%;
  height: 100%;

  .map-conatainer {
    position: relative;
    width: 100%;
    height: 100%;
  }
}

dialog {
  min-width: 320px;
  width: 90%;
  /* max-width: 1400px; */
  min-height: 320px;
  height: 85%;
  /* max-height: 1200px; */
  padding: 0;
  border: 0;
  border-radius: 8px;
  box-shadow: 0 4px 12px -4px var(--secondary);
}
.geoDistanceConfig {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: absolute;
  top: 0;
  left: 0;
  margin: 1.6rem;
  padding: 1rem;
  width: 20rem;
  max-width: 500px;
  border-radius: 4px;
  background: var(--white);
  box-shadow: 0 4px 12px -4px var(--secondary);
  z-index: 2;
}
.geoDistanceConfig > .geoDistanceHeader > h1 {
  font-size: 2rem;
  margin: 0;
}
.geoDistanceRatio > label {
  font-size: 1rem;
}
.geoDistanceRatio > .geoDistanceRatio__range {
  display: flex;
  justify-content: space-between;
}
.geoDistanceLocation {
  display: flex;
  flex-direction: column;
}
.geoDistanceLocationInput {
  height: 4rem;
  width: 100%;
}
.geoDistanceLocationInput > textarea {
  height: 100%;
  width: 100%;
  resize: none;
  padding: 0.25rem 0.5rem;
  overflow-y: hidden;
}
.geoDistanceLocationInput > textarea:focus {
  outline-color: var(--primary);
}
</style>
